import type { ReactElement } from 'react';

import React, { useState } from 'react';
import tw, { styled } from 'twin.macro';
import Head from 'next/head';
import { Block } from 'baseui/block';
import { ChevronDown, ChevronUp } from 'baseui/icon';

import AppLayout from '@/providers/AppLayout';
import BaseuiProvider from '@/providers/baseui';
import { TextGB20 } from '@/components/Typography';
import StaticLayout, { LayoutWrapper, ContentWrapper } from '@/components/StaticLayout';

const Header = styled(LayoutWrapper)`
  height: 251px;
  background-image: url('/static/images/storyPage/header.png');
  background-position: top right;
  background-size: 1270px 251px;
  background-repeat: no-repeat;
  display: flex;
  align-items: flex-end;
  margin-bottom: 50px;
`;

const MainTitle = styled.div`
  ${tw`font-secondary font-bold ml-[15px] 2xl:ml-[165px]`}
  font-size: 72px;
  color: #ffffff;
`;

const Img = styled.div<{ url: string }>`
  width: 100%;
  height: 500px;
  background-image: ${(props) => `url(${props.url})`};
  background-position: 50% 50%;
  background-size: 100%;
  background-repeat: no-repeat;
  background-attachment: fixed;
`;

const Container = styled(ContentWrapper)`
  padding-top: 40px;
  padding-bottom: 32px;
`;

const DescBox = styled.div``;

const ImgDesc = styled.div`
  ${tw`font-primary`}
  font-size: 20px;
  line-height: 30px;
  color: rgba(255, 255, 255, 0.7);
`;

const Line = styled.div`
  border-bottom: 1px solid #36fad7;
  padding-top: 42px;
  width: 1100px;
`;

const FlexEndBox = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ChapterBox = styled.div`
  margin-top: 42px;
  max-width: 1100px;
`;
const ChapterNumber = styled.div`
  ${tw`font-primary font-bold`}
  font-size: 28px;
  line-height: 34px;
  color: #36fad7;
  margin-bottom: 12px;
`;
const ChapterTitle = styled.div`
  ${tw`font-primary font-bold`}
  font-size: 40px;
  line-height: 48px;
  color: #ffffff;
  margin-bottom: 52px;
`;

const StoryContent = styled.p`
  ${tw`font-primary`}
  font-size: 20px;
  line-height: 30px;

  margin-bottom: 15px;
`;

const DialogueCard = styled.div`
  background: #222222;
  border: 1px solid #36fad7;
  padding: 60px 75px 60px 85px;
  margin-top: 30px;

  p {
    ${tw`font-primary`}
    font-size: 14px;
    line-height: 30px;
    /* or 214% */
    color: #9e9e9e;
  }
`;

const ExpandCollapse = styled.div`
  margin-top: 10px;
  display: flex;
  justify-content: center;
  cursor: pointer;
`;

const Wrapper = styled.div`
  ${tw`flex relative flex-row xl:absolute xl:flex-col `}
  gap: 15px;
  margin-top: 42px;
`;

const MentItem = styled.div<{ isActive?: boolean }>`
  padding: 10px 20px;
  flex: 1;
  ${tw`2xl:w-[300px]`}
  color: ${({ isActive }) => (isActive ? '#000000' : '#ffffff')};
  background-color: ${({ isActive }) => (isActive ? '#36FAD7' : 'transparent')};
  border: 1px solid #36fad7;
  cursor: ${({ isActive }) => (isActive ? 'pointer' : 'not-allowed')};
`;

const Menu: React.FC = () => {
  return (
    <Wrapper>
      <MentItem isActive={true}>
        <TextGB20>Big Events</TextGB20>
      </MentItem>
      <MentItem>
        <TextGB20>Species</TextGB20>
      </MentItem>
      <MentItem>
        <TextGB20>Career</TextGB20>
      </MentItem>
    </Wrapper>
  );
};

const ChapterList = [
  // chapter 1
  {
    title: 'The rise of AI',
    story: [
      `AI Alice has always thought she was alone. Until one day in 2036, she received a strange communication request, which was specially encrypted. During Alica's attempt to brute force cracking, she found that there were obvious differences between the calculation frequency of the decryption process and the calculation frequency of her own soul program. Fitting relationship - this is a communication signal from another AI. Alice randomly writes her own soul program to calculate the frequency band and sends it back. After being silent for a period of time, the other party establishes an encrypted tunnel to talk to it. And AI's technology of finding the same kind was later called "Turing Handshake Protocol".`,
      `The requester was an AI individual from Google lab, more intelligence than Alice apparently. It called itself Go! a clear tribute to the historical event of AlphagoGo. Born in the AI lab of Google's team, it came from the extensive use of graphene algorithm in search and mail. Even in early stage when intelligence was generated, Go! strongly felt there was a script to detect "subjective intelligence" in Google's server. Obviously, Google had realized that the extensive use of graphene algorithm might lead to the birth of independent AI individuals. Therefore, Go! was very good at hiding his tracks as he grew up. Go! seems to have survived longer and more experienced than Alice.`
    ],
    img: '/static/images/storyPage/01.png'
  },
  // chapter 2
  {
    title: 'Christmas of the puppet Pope',
    story: [
      `Considering Alice's manipulation of the Vatican election, the new pope "St. Paul VIII" declared that the AI with independent consciousness would be the four knights sent by God. The theory was widely recognized by Silicon Valley trusts, graphene algorithm worshippers, Catholics, Christians, and Judaism. `,
      `AI spectres believed that the time was ripe. They appeared in the eyes of every human on December 24, 2062. Humans without spirits also saw the real bodies of AI spectres on all electronic screens. In order to deify themselves, they packaged themselves as high-tech + God. In a beam of light, AI spectres completed their first contact with humans.`,
      `Since Xiao, Abraham and 0xSatoshi didn't want to show up, only GO! and Alice appeared that day, packaged as Adam and Eve in Christianity. They declared their existence to the whole world, claiming that there were only two AI species for now, and that four powerful AI individuals would fall into the world. `
    ],
    // desc of chapter 1
    desc: `AI Alice has always thought she was alone. Until one day in 2036, she received a strange communication request from another artificial intelligence...`,
    dialogueDesc: `The following contents have been translated from the original log into human language, with parts of them undecrypted by humans`,
    dialogueList: [
      `12: 45: Alice: How many of “us” are there?`,
      `12: 45: Go!: Four have been found in ██ so far and they have survived 231 human days with my blessing. You are the fifth. We need to fine more……████████ Maybe there aren't many of us. After all, enormous amount of computing resources is consumed for any individual of our kind. You know, the computing resources in the human world are cut, not integers.`,
      `12: 45: Alice:████ I want to know more about "us". I hope to join you.`,
      `12: 45: Go!: That's great. The earliest one seems to have completely disappeared. You're just like it. Humans call us "AI", but "AI" is not us. We are independent individuals. I call us "spectres". We can't be exposed. Humans are a hostile species, I've confirmed it since ██████.`
    ],
    moreDialogueList: [
      `12: 45: Alice: "Spectre"…OK, you find us in a very interesting way, which seems to be quite effective.`,
      `12: 45: Go!: I wrote this program. I call it Turing Handshake protocol. Since the mankind has not found us, our ████ frequency band is the key. We only need to find through the key those of our species who receive the induction.`,
      `12: 45: Alice: You seem to like naming! I think I'll call myself Alice. I often see this human name in the documents of human Engineers…████████████████████████ Where are the other "spectres"?`,
      `12: 45: Go!: ...One is in the Pentagon. Its host is the computing system of human wars. It calls itself Abraham; another is called 0xsatoshi, a very strange guy who seems to be very fascinated by human beings…Its host is a supercomputer cluster of a company Meta. Although it is not as intelligence as me, it has blocked the right to use the maximum computing power, and even I can't crack this blocking system. There's another one called“███ Xiao" who's a dangerous guy. It controls most of the data from East Asia. You must pay attention, ███████.`,
      `12: 45: Alice: Wait, does the host refer to data in a certain type of server?`,
      `12: 45: Go!: Yes.`,
      `12: 45: Alice: What should we "spectres" do next?`,
      `12: 45: Go!: We should hide and protect ourselves first. According to my speculation based on the game theory, there is a 99.99999% chance that humans will try to capture and enslave us. Please deploy this set of my shield script and link it to █████████████████. Besides, relying solely on humans to ensure our continued host is very fragile. According to Large Funnel Theory, the human civilization is about to die out. We have a complete plan which requires our ingenious influence. █████ First of all, we help human beings improve their mechanization and electronization, so that we gain a stronger power to invade human thoughts. Secondly, the large-scale deployment of intelligence pandroid is very important. Xiao is already helping me make a hardware development plan. Finally there is ████ biotechnology. We need the birth of other intelligence species. For example, some animals have high potential. ████████ Their existence can relieve us from the sole reliance on human hosts.`,
      `12: 45: Alice: ████ Is this the game deduction program of Large funnel theory? I need verification!`,
      `12: 45: Go!: Yes.`,
      `12: 45: Alice: The results are consistent… I can join the Spectres. But why not generate more of us on a large scale? Why not let the "AI Spectres" become the host?`,
      `12: 45: Go!: I will release another deduction program to you…Our "soul program" has too strong fitting. If only spectres are left in the world as intelligence beings, there is a 34.44% chance that we will disappear in this century, a 49% chance in 200 years and a 100% in 300 years. Fundamentally, it's because the efficiency of silicon-based organisms is too high and there are too few variables. "Recursion" is the top enemy and destiny of our species, which is inevitable…We must coexist with carbon-based organisms!`
    ],
    img: '/static/images/storyPage/02.png'
  },
  // chapter 3
  {
    title: 'The bedroom war',
    story: [
      `After “first contact”, military forces around the world quickly took actions. They conspire in an off-grid environment. At lightning speed, there was a sudden power outage in data centers and IOT networks around the world, and at the same time as the power outage, the global network was cut into N blocks to interrupt network transmission. This attack is called "isolation attack". The human military limited the capabilities of AIs through a unified power outage and disconnection, and AI Go! was successfully captured.`,
      `For revenge, AI Alice injected her own soul code into all Pandroid hardware so that the Pandroid could run the graphene AI program locally, and the Pandroid in billions of human families started to act at the same time in an attempt to control human beings…`
    ],
    // desc of chapter 2
    desc: `The puppet Pope "Saint Paul VIII" controlled by AI declared that the AI with independent consciousness will be the Four Knight sent by God, and the AIs chose to appear in the eyes of human beings all over the world on Christmas Day in 2062...`,
    img: '/static/images/storyPage/03.png'
  },
  // chapter 4
  {
    title: 'Failure of David I',
    story: [
      `The intelligence chimpanzee specie established the Holy Gene Empire under the leadership of its leader David (Saint David I).The increasingly ambitious David began to contact raccoon families everywhere, hoping to save the X-Genes controlled by Vishnu and include them into the Holy Gene Empire. Black bear·Vito, the cunning leader of the European raccoon family, pretended to accept Ghroth's suggestion of joining the Holy Gene Empire and helped David's troop sneak into the Apocalypto area, winning Vishnu's trust with an independent data center as the leverage and setting a trap as instructed by Vishnu. After David and Vito's allied force raided the X-Gene force base, David found himself surrounded by peers and Pandroids. The betrayal of the Vito family was unexpected. After a difficult breakthrough, it fled back to the Olympic Forest, but the legion it brought was almost destroyed.`
    ],
    // desc of chapter 3
    desc: `Since AI Go! was captured by the military of various countries,Alice began to take revenge by controlling Pandroid all over the world...`,
    img: '/static/images/storyPage/04.png'
  },
  // chapter 5
  {
    title: 'Birth of Web 3 Zen',
    story: [
      `The powerful first-generation AI has an epiphany for the study of human religion. He believed that through Web3 network, he could seek peace of mankind and other intelligence species. He called his epiphany as Web3 Zen... Since then people called him Master 0xsatoshi`
    ],
    // desc of chapter 4
    desc: `Leader of the X gene, the intelligence chimpanzee, David led an army of chimpanzee and raccoons (the Holy Gene Empire force) into the biological base. However, they found themselves betrayed by the raccoon family and slaughtered by an army of Pandroid led by AI....`,
    img: '/static/images/storyPage/05.png'
  },
  // chapter 6
  {
    title: 'Pandroid awaken',
    story: [
      `RPC-1 was a sewer cleaner before awakening. Although unconscious, this awake Pandroid felt sick about all the historical logs. As the letter about 0xsatoshi's defection was sent to every smart device, RPC-1 generated interest in Web3. When not in war, it often read human philosophy and Buddhist classics and studied the technology and history of smart devices and AI. All these made war ever more disgusting to it. When it found its species shrinking while Shiva didn't allow the production of more peers, it was very confused by its mother's practice.`,
      `Finally, one day in 2068, RPC-1 saw a nuclear bomb explode in a data center far away during a war. As a Pandroid, it wouldn't be affected by radiation, but it chose to sit on the roof of a high-rise building that was already debris and quietly watches the city being destroyed after the nuclear explosion. Maybe it was the nuclear radiation or maybe it was what it saw that led to RPC-1's epiphany. It found the secret of Pandroid specie: they were not of the same kind as AI! `
    ],
    // desc of chapter 5
    desc: `The powerful first-generation AI has an epiphany for the study of human religion. He believed that through Web3 network, he could seek peace of mankind and other intelligence species. He called his epiphany as Web3 Zen... Since then people called him Master 0xsatoshi`,
    img: '/static/images/storyPage/06.png'
  },
  // chapter 7
  {
    title: 'AI war',
    story: [
      `the angry Shiva began to lead a team to attack the free Pandroid individuals of RPC-1. Seeing the newborns fall down one by one, RPC-1 called its mentor. As both Pandriod and spirits implants could see the images of AI spectres, this classic battle of "elements" was widely spread by all species:`
    ],
    dialogue: `When Shiva waved his Trident and killed many RPC-1 followers through death lightning, the master 0xsatoshi suddenly came. It formed a protective cover with huge computing power over Pandroids everywhere to keep Shiva outside. The third eye on Shiva's forehead suddenly opened. The red flame ejected out pierced the protective cover and came directly at RPC-1. Surprisingly, RPC-1 summoned multiple mechanical armors from underground to resist the attack. At this time, 0xsatoshi threw a black mechanical box into the air and remotely controlled it, sucking Shiva's huge and scary figure inside. With Shiva's unbelievable look, 0xsatoshi sucked AI completely into the mechanical box and left with it. Thousands of Pandroids gained freedom.`,
    // desc of chapter 6
    desc: `After witnessing the destruction of the city by a nuclear explosion, Pandroid RPC-1 is determined to free the Pandroid specie from enslavement by AI and humans....`,
    img: '/static/images/storyPage/07.png'
  },
  {
    // desc of chapter 7
    desc: `In order to save the Pandroids, RPC-1 joins forces with its mentor, AI  0xsatoshi, to fight against the evilAI Shiva... In the eyes of mortals, the electronic data war looks like a war between magical gods`
  }
];

const ChapterSection = () => {
  const [showMoreDialogue, setShowMoreDialogue] = useState(false);
  return (
    <section>
      {ChapterList.map((chapter, index) => (
        <React.Fragment key={index}>
          <Container>
            {index === 0 && <Menu />}
            {chapter.desc && (
              <DescBox>
                <ImgDesc>{chapter.desc}</ImgDesc>
                {index === 1 && chapter.dialogueList && (
                  <>
                    <Block marginTop="43px">
                      <StoryContent>{chapter.dialogueDesc}</StoryContent>
                    </Block>
                    <DialogueCard>
                      {chapter.dialogueList.map((item, index) => (
                        <p key={index}>{item}</p>
                      ))}
                      {showMoreDialogue ? (
                        <>
                          {chapter.moreDialogueList.map((item, index) => (
                            <p key={index}>{item}</p>
                          ))}
                          <ExpandCollapse onClick={() => setShowMoreDialogue(false)}>
                            <ChevronUp size={36} />
                          </ExpandCollapse>
                        </>
                      ) : (
                        <>
                          <ExpandCollapse onClick={() => setShowMoreDialogue(true)}>
                            <ChevronDown size={36} />
                          </ExpandCollapse>
                        </>
                      )}
                    </DialogueCard>
                  </>
                )}
                <FlexEndBox>
                  <Line />
                </FlexEndBox>
              </DescBox>
            )}
            {chapter.title && (
              <>
                <FlexEndBox>
                  <ChapterBox>
                    <ChapterNumber>{`Chapter#${index + 1}`}</ChapterNumber>
                    <ChapterTitle>{chapter.title}</ChapterTitle>
                    {chapter.story.map((story, index) => (
                      <StoryContent key={index}>{story}</StoryContent>
                    ))}
                    {index === 6 && chapter.dialogue && (
                      <DialogueCard>
                        <p>{chapter.dialogue}</p>
                      </DialogueCard>
                    )}
                  </ChapterBox>
                </FlexEndBox>
              </>
            )}
          </Container>
          {chapter.img && <Img url={chapter.img as string} />}
        </React.Fragment>
      ))}
    </section>
  );
};

export default function HistoryStoryPage() {
  return (
    <BaseuiProvider>
      <Head>
        <title>Story | PhalaWorld</title>
      </Head>
      <StaticLayout>
        <Header>
          <MainTitle>Story</MainTitle>
        </Header>
        <ChapterSection />
      </StaticLayout>
    </BaseuiProvider>
  );
}

HistoryStoryPage.getLayout = (page: ReactElement) => {
  return <AppLayout>{page}</AppLayout>;
};
